import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { format } from 'date-fns';
import type { AssigneeDoc, ExecutionStatus, ExecutionType } from '@mct/shared';
import { cn } from '../../lib/utils';
import { AssigneeChip } from '../common/AssigneeChip';
import { ExecutionKindChip } from '../common/ExecutionKindChip';
import { StatusBadge } from '../StatusBadge';

export interface TaskNodeData extends Record<string, unknown> {
  name: string;
  /** Structured top-level category — the only category value the node
   *  renders as a chip. */
  category: string;
  /** Original 大項目 表記 the category was derived from. Surfaced only as
   *  a hover tooltip on the category chip; `null` for hand-created tasks. */
  subcategory?: string | null;
  /**
   * Snapshot of the resolved assignee from `/projects/{pid}/assignees`.
   * `null` when the task is unassigned. The DAG node renders the same
   * coloured chip as the WBS / migration-task list when present.
   */
  assignee: Pick<AssigneeDoc, 'name' | 'colorIndex'> | null;
  executionType: ExecutionType;
  status: ExecutionStatus;
  completedAt: Date | null;
  backlogCount: number;
  cycleDetected: boolean;
  /**
   * Highlight overlays driven by the page-level `HighlightControls`.
   * `null` when the controls are inactive — the card renders normally
   * in that case. When active, the page sets exactly one of
   * `dimmed` (no match) or `highlighted` (matches every selected
   * category). See `lib/highlight.ts` for the matching contract.
   */
  dimmed?: boolean;
  highlighted?: boolean;
  /** Tailwind ring colour utility for the highlighted outline (e.g.
   *  `ring-blue-300`). Falls back to a neutral slate when omitted. */
  ringColorClass?: string | null;
}

function TaskNodeImpl({ data, selected }: NodeProps) {
  const d = data as unknown as TaskNodeData;
  const isSkipped = d.status === 'skipped';
  const ring = d.highlighted
    ? `ring-2 ring-inset ${d.ringColorClass ?? 'ring-slate-400'}`
    : selected
    ? 'ring-2 ring-slate-400'
    : '';

  return (
    <div
      data-testid="dag-task-node"
      data-highlighted={d.highlighted ? 'true' : undefined}
      data-dimmed={d.dimmed ? 'true' : undefined}
      className={cn(
        'w-[240px] rounded-md border bg-white px-3 py-2 text-xs shadow-sm transition-shadow',
        ring,
        d.cycleDetected
          ? 'border-red-500 border-dashed'
          : 'border-slate-200',
        // Tasks that don't apply to the current run render dim so the DAG still
        // shows them (structural context) without pulling attention.
        isSkipped ? 'bg-slate-50 opacity-50' : '',
        // When highlight controls are active, non-matching cards dim
        // the same way (opacity + grayscale) so the contrast against
        // matching cards reads at a glance without hiding edges.
        d.dimmed ? 'opacity-50 grayscale' : '',
      )}
      title={isSkipped ? 'この Run では対象外のタスクです' : d.name}
    >
      <Handle type="target" position={Position.Left} className="!bg-slate-400" />
      <div className="flex items-start gap-1.5">
        <span className="line-clamp-2 min-w-0 flex-1 break-words font-semibold leading-snug">
          {d.name}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
        {/* Read-only execution-kind chip. Same color language as the
            detail panel (red 手作業 / 緑 自動化 / 黄 ハイブリッド) so the
            chart card and the detail panel agree at a glance. The
            previous lucide icons (Bot / User / Wrench) carried the same
            information but with a per-icon mental map; the colored
            chip makes the kind scannable across a wall of cards. */}
        <ExecutionKindChip value={d.executionType} />
        {d.assignee ? (
          <AssigneeChip assignee={d.assignee} name={d.assignee.name} />
        ) : null}
        {d.category ? (
          <span
            className="rounded bg-slate-100 px-1"
            title={d.subcategory ?? undefined}
          >
            {d.category}
          </span>
        ) : null}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <StatusBadge status={d.status} />
          {d.status === 'done' && d.completedAt ? (
            <span className="tabular-nums">✅ {format(d.completedAt, 'M/d')}</span>
          ) : null}
        </span>
        {d.backlogCount > 0 ? (
          <span className="rounded bg-blue-50 px-1 text-blue-700">
            Backlog:{d.backlogCount}
          </span>
        ) : null}
      </div>
      {d.cycleDetected ? (
        <div className="mt-1 text-[10px] font-medium text-red-600">
          ⚠️ 循環検知
        </div>
      ) : null}
      <Handle
        type="source"
        position={Position.Right}
        className="!bg-slate-400"
      />
    </div>
  );
}

export const TaskNode = memo(TaskNodeImpl);
